/**
 * Payment Helper Utilities
 * Shared functions for payment processing and display
 */

// Payment narrations that represent actual cash outflows to investors
export const PAYMENT_NARRATIONS = [
  'Interest Payment',
  'Capital Payment',
  'Adhoc Capital Withdrawal',
  // Feature 30 Phase 4. A broker fee payment is a cash outflow like any other, so it belongs here
  // — verified before adding that 'Broker Fee Payment' did NOT already match: the test below is
  // .includes(), and none of the three above is a substring of it.
  //
  // ⚠ This is one of FOUR places that enumerate payment kinds; the other three are in
  // pages/PaymentMatching.js (the tab registry, matchTypeForRow, and the endpoint map). Omitting
  // any one of them misclassifies silently rather than erroring.
  'Broker Fee Payment',
];

/**
 * Check if a narration represents an actual payment (cash outflow)
 * @param {string} narration - The narration text from amortization schedule
 * @returns {boolean} True if this is a payment narration
 */
export const isPaymentNarration = (narration) => {
  if (!narration) return false;
  return PAYMENT_NARRATIONS.some(n => narration.includes(n));
};

/**
 * Calculate the difference in days between two dates
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {number} Absolute difference in days
 */
export const differenceInDays = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  const diffTime = Math.abs(d1 - d2);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Add days to a date
 * @param {Date} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date} New date with days added
 */
export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Get payment status color for MUI Chip
 * @param {string} status - Payment status
 * @returns {string} MUI color name
 */
export const getPaymentStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'paid':
    case 'matched':
    case 'received':
      return 'success';
    case 'overdue':
    case 'rejected':
    case 'failed':
      return 'error';
    case 'pending':
    case 'partial':
      return 'warning';
    default:
      return 'default';
  }
};

/* ============================================================================
   MULTI-INVESTOR DISPLAY (T3-109 / T3-110)
   ============================================================================
   The payment feeds emit three multi-investor columns — is_multi_investor,
   investor_count and investor_names — alongside the per-row investor_name.
   These two resolvers are the SINGLE definition of how that is turned into a
   label and into search text; every consumer must go through them, because the
   same fallback was previously open-coded as `investor_name || '-'` in four
   places and a multi-investor contract rendered as a bare dash in all of them.

   WHY A ROW CAN HAVE NO investor_name: generateMultiInvestorSchedule runs the
   engine once per investor, so a multi-investor contract normally holds one
   schedule row PER INVESTOR and each names its own investor. But where the
   schedule was written at CONTRACT level instead (dev MTN-SZ-2026-0001), both
   the schedule and the contract investor keys are NULL by design, so there is
   genuinely no single investor to name — hence the count-based fallback rather
   than inventing one.
   ============================================================================ */

/**
 * Display label for the investor on a payment-feed row.
 *
 * Mirrors the canonical chip in ContractList.js so the two screens agree.
 *
 * @param {object} row - A payment-feed row (schedule row, WHT entry, or receipt)
 * @returns {string} The investor name, "Multiple (N)", or '-'
 */
export const investorLabel = (row) => {
  if (row?.investor_name) return row.investor_name;
  if (row?.is_multi_investor) {
    const n = parseInt(row.investor_count, 10) || 0;
    // "Multiple (0)" would be a lie dressed as data — a multi-investor contract
    // with no ACTIVE junction rows is a data fault, so degrade to the unqualified
    // label rather than displaying a count we know to be wrong.
    return n > 0 ? `Multiple (${n})` : 'Multiple';
  }
  return '-';
};

/**
 * Search text for the investor on a payment-feed row.
 *
 * Includes investor_names so a contract whose schedule is contract-level (and so
 * has no investor_name) is still findable by ANY of its holders' names — the
 * search haystack is string-based, so a count-only projection would leave those
 * contracts unfindable by investor entirely.
 *
 * ⚠ investor_names is DISPLAY/SEARCH ONLY — never a join key and never an
 * identity. It is denormalised from the junction at read time.
 *
 * @param {object} row - A payment-feed row
 * @returns {string} Space-joined searchable investor text
 */
export const investorSearchText = (row) =>
  `${row?.investor_name || ''} ${row?.investor_names || ''}`;

/**
 * Partial-settlement state of a payment-feed row or group (T3-111).
 *
 * T3-103 keeps a partly-paid line in the queue so the shortfall can be topped up, but until
 * T3-111 the feed projected no way to SAY so — a line with cash already against it rendered
 * identically to an untouched one, and an operator paying the displayed scheduled amount would
 * over-pay by whatever had already been received (dev Med-SZ-2026-0001: 6,000.00 of a 6,934.93
 * line already settled, so only 934.93 was really owed).
 *
 * `outstanding` is the figure the operator should actually pay, and is what the match dialog
 * seeds — never `scheduled_amount` on a part-paid row.
 *
 * @param {object} row - A payment-feed row or a group produced by groupScheduleRowsByContractDate
 * @returns {{scheduled: number, matched: number, outstanding: number, isPartial: boolean}}
 */
export const partialPaymentInfo = (row) => {
  const scheduled = parseFloat(row?.scheduled_amount) || 0;
  const matched = parseFloat(row?.matched_amount) || 0;
  // 0.01 tolerance mirrors the DB coverage predicate so the UI and the query agree on
  // "is anything still owed" rather than disagreeing over a rounding cent.
  const isPartial = matched > 0.005 && matched < scheduled - 0.01;
  return {
    scheduled,
    matched,
    outstanding: +Math.max(0, scheduled - matched).toFixed(2),
    isPartial,
  };
};

/**
 * The composition filters offered above the matching queues.
 *
 * Values are the chip keys; the predicates are the single definition of what each means, so the
 * chip counts and the filtered rows can never disagree.
 */
export const ROW_FILTERS = {
  single: {
    label: 'Single',
    group: 'composition',
    // A grouped line is by definition multi-investor, so exclude it explicitly rather than
    // relying on is_multi_investor alone (a group's head row carries the flag).
    test: (row) => !row?.isGroup && !row?.is_multi_investor,
  },
  multiple: {
    label: 'Multiple',
    group: 'composition',
    test: (row) => !!row?.isGroup || !!row?.is_multi_investor,
  },
  partial: {
    label: 'Partially Paid',
    group: 'modifier',
    test: (row) => partialPaymentInfo(row).isPartial,
  },
};

/** Keys belonging to the mutually-exclusive COMPOSITION group (a row is Single XOR Multiple). */
export const COMPOSITION_KEYS = Object.keys(ROW_FILTERS)
  .filter((k) => ROW_FILTERS[k].group === 'composition');

/**
 * Apply the active filter chips to a row set.
 *
 * TWO AXES, COMBINED WITH AND — not one flat OR.
 *
 *   composition: Single | Multiple   (mutually exclusive — a row is one or the other, never both)
 *   modifier:    Partially Paid      (narrows whichever composition is active)
 *
 *   nothing            -> everything
 *   Multiple           -> all multi-investor lines
 *   Partially Paid     -> all part-paid lines, single and multiple alike
 *   Multiple + Partial -> ONLY multi-investor lines that are ALSO part-paid
 *
 * The previous implementation OR-ed all three, so "Multiple (2) + Partially Paid (2)" returned
 * FOUR rows — two multi-investor lines that were not part-paid, plus two part-paid lines that were
 * single. Neither is what selecting both asks for.
 *
 * Exclusivity lives HERE, in the data, rather than in the chip click handler, so the counts and the
 * filtering are computed from one definition and cannot disagree.
 *
 * An empty selection means NO FILTER, never "no rows" — a filter nobody set must never hide work.
 *
 * ⚠ Apply this AFTER grouping. Filtering raw feed rows would count a multi-investor contract once
 * per investor and could leave a group partially represented.
 *
 * @param {Array<object>} rows - already-grouped rows
 * @param {Array<string>} active - selected keys from ROW_FILTERS
 * @returns {Array<object>}
 */
export const applyRowFilters = (rows, active) => {
  if (!Array.isArray(rows)) return [];
  const keys = (active || []).filter((k) => ROW_FILTERS[k]);
  if (keys.length === 0) return rows;

  const composition = keys.filter((k) => ROW_FILTERS[k].group === 'composition');
  const modifiers = keys.filter((k) => ROW_FILTERS[k].group === 'modifier');

  return rows.filter((row) => {
    // Composition is exclusive, but tolerate more than one key arriving (a stale URL, a future
    // third option) by OR-ing within the axis rather than returning nothing.
    if (composition.length > 0 && !composition.some((k) => ROW_FILTERS[k].test(row))) return false;
    // Every active modifier must hold — they narrow, they do not widen.
    return modifiers.every((k) => ROW_FILTERS[k].test(row));
  });
};

/**
 * Count what each chip would yield GIVEN the current selection.
 *
 * A chip's number must predict what clicking it produces. Counting each filter standalone made
 * "Partially Paid (2)" sit next to an active "Multiple" and then return zero rows — the number
 * promised something the filter could not deliver, which is the same class of defect as the OR bug
 * above, one level up.
 *
 * For a composition chip: the count if that composition were selected, with the active modifiers
 * still applied. For a modifier: the count if it were added to the current selection.
 *
 * @param {Array<object>} rows - already-grouped rows, UNFILTERED
 * @param {Array<string>} active - currently selected keys
 * @returns {Object<string, number>} keyed by filter key
 */
export const rowFilterCounts = (rows, active) => {
  const list = Array.isArray(rows) ? rows : [];
  const keys = (active || []).filter((k) => ROW_FILTERS[k]);
  return Object.keys(ROW_FILTERS).reduce((acc, key) => {
    const isComposition = ROW_FILTERS[key].group === 'composition';
    // Selecting a composition replaces the other; selecting a modifier adds to what is there.
    const hypothetical = isComposition
      ? [key, ...keys.filter((k) => ROW_FILTERS[k].group !== 'composition')]
      : Array.from(new Set([...keys, key]));
    acc[key] = applyRowFilters(list, hypothetical).length;
    return acc;
  }, {});
};

/**
 * Human label for a pending change's type, on the approvals list and detail view.
 *
 * A payment carry-forward is submitted as change_type 'schedule_override' ON PURPOSE: that is what
 * it is — the shortfall row is written down and the residual rolls onto the next payment marker —
 * and reusing the type means the EXISTING approval executor applies it. (A bespoke change_type was
 * tried first and had no case in approvalService's switch, so approving one fell through to "no
 * execution required": the request was marked executed, the schedule was never touched, and the
 * shortfall was silently discarded.)
 *
 * The cost of that correct decision is that these entries render as a plain "schedule override" and
 * become indistinguishable from an ordinary one. `change_data.carry_forward` is the flag that tells
 * them apart, so the label reads from it here — DISPLAY ONLY. Nothing about the change_type, the
 * executor or the approval path depends on this function.
 *
 * @param {object} item - a pending_changes row (needs change_type and change_data)
 * @returns {string} label for the type chip
 */
export const changeTypeLabel = (item) => {
  const raw = String(item?.change_type || '').replace(/_/g, ' ');
  if (item?.change_type === 'schedule_override' && item?.change_data?.carry_forward) {
    return 'carry forward';
  }
  return raw;
};

/**
 * Split `total` across `weights` pro-rata, with the residual on the largest weight.
 *
 * Used for BOTH the contract-currency legs and the foreign settlement amount of a grouped match,
 * so the two columns are apportioned by the identical rule and cannot drift a cent apart.
 *
 * The residual goes to the LARGEST weight (ties → lowest index) rather than simply the last row:
 * row order is a presentation detail, and letting it decide who absorbs a rounding cent would make
 * the split depend on how the table happened to be sorted. Same rule the amortization engine uses
 * for multi-investor adhoc interest.
 *
 * @param {number} total - the amount to divide
 * @param {Array<number>} weights - basis per recipient (e.g. each investor's outstanding)
 * @returns {Array<number>} parts summing EXACTLY to `total`
 */
export const apportionByWeight = (total, weights) => {
  const t = parseFloat(total) || 0;
  const w = (weights || []).map((v) => Math.max(0, parseFloat(v) || 0));
  if (w.length === 0) return [];
  const denom = w.reduce((s, v) => s + v, 0);
  if (denom <= 0) return w.map(() => 0);

  const parts = w.map((v) => +((t * v) / denom).toFixed(2));
  const drift = +(t - parts.reduce((s, v) => s + v, 0)).toFixed(2);
  if (drift !== 0) {
    let idx = 0;
    for (let i = 1; i < w.length; i += 1) if (w[i] > w[idx]) idx = i;
    parts[idx] = +(parts[idx] + drift).toFixed(2);
  }
  return parts;
};

/**
 * Seed the two-leg split of a lump sum for an interest + withholding-tax match (T3-105).
 *
 * TAX LEG FIRST — the opposite of a Combined (interest/capital) match, deliberately.
 *
 * Interest-first seeding took `min(cash, expectedInterest)`, so on ANY short pay the interest leg
 * absorbed everything and the tax leg seeded 0.00 — e.g. 1,500.00 against a 1,613.70 line with
 * 127.40 of tax gave interest 1,500.00 / tax 0.00, while the per-investor table below allocated the
 * full 127.40. Two allocation blocks showing two different answers at once, and the submitted
 * payload took its WHT figure from THIS one — so the match posted the interest with no tax
 * remitted.
 *
 * Tax is all-or-nothing (it cannot be partially remitted and topped up later, because the pending
 * WHT feed has no coverage test), so it cannot absorb a shortfall. Interest can, because T3-103
 * lets a short-paid interest line stay in the queue and be topped up. The shortfall therefore has
 * to land on interest.
 *
 * @param {number} cash - the lump sum received
 * @param {number} whtAmount - the tax due on the line
 * @returns {{interest: number, tax: number}} legs summing exactly to `cash`
 */
export const seedInterestWhtLegs = (cash, whtAmount) => {
  const c = Math.max(0, parseFloat(cash) || 0);
  const w = Math.max(0, parseFloat(whtAmount) || 0);
  const tax = Math.min(c, w);
  return { interest: +(c - tax).toFixed(2), tax: +tax.toFixed(2) };
};

/**
 * Adapt a payment-feed row to the field names the shared Overdue/Upcoming tables expect.
 *
 * The feeds emit scheduled_amount/currency; those tables read payment_amount/currency_code.
 * This shim was open-coded identically at SIX call sites in PaymentMatching.js, so any new
 * column had to be threaded through all six or silently go missing in some tabs. One
 * definition now.
 *
 * @param {object} r - A raw payment-feed row
 * @returns {object} The row with table-facing aliases added
 */
export const toTableRow = (r) => ({
  ...r,
  payment_date: r.payment_date,
  payment_amount: r.scheduled_amount,
  currency_code: r.currency,
});

/**
 * Collapse per-investor schedule rows into ONE line per (contract, due date, narration).
 *
 * WHY THIS IS CLIENT-SIDE, NOT A SERVER GROUP BY
 * -----------------------------------------------
 * The T3-103/T3-106 coverage anti-join in fn_payment_get_schedule_rows is a PER-SCHEDULE-ROW
 * test, and it is duplicated VERBATIM in sp_payment_match_inline's guard — the two are
 * required to stay in lockstep. Aggregating coverage server-side would either hide a settled
 * sibling inside a group or drag a whole group back into the queue because one sibling is
 * unsettled, AND would force the write guard to aggregate too (a third copy of the predicate
 * is precisely how these drift). The write path needs the individual schedule_ids regardless,
 * so nothing is saved by aggregating upstream. Grouping here keeps the DB emitting the truth —
 * one row per investor — and makes the collapse a presentation concern.
 *
 * KEY is (contract_id, payment_date, narration) — contract_id, NOT contract_reference, which
 * is display text and can repeat across tenants' imports.
 *
 * A SINGLE-ROW GROUP IS RETURNED UNGROUPED (isGroup: false). That is what keeps every
 * single-investor contract — and a multi-investor contract whose schedule is contract-level
 * rather than per-investor split — on byte-for-byte their current behaviour, by construction
 * rather than by a special case.
 *
 * @param {Array<object>} rows - Payment-feed rows (already search-filtered)
 * @returns {Array<object>} One entry per contract/date/narration, newest ordering preserved
 */
export const groupScheduleRowsByContractDate = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const map = new Map();
  const order = [];
  for (const r of rows) {
    const key = `${r.contract_id}|${r.payment_date}|${r.narration || ''}`;
    if (!map.has(key)) { order.push(key); map.set(key, []); }
    map.get(key).push(r);
  }

  return order.map((key) => {
    const members = map.get(key);
    const head = members[0];

    if (members.length === 1) {
      return { ...head, _groupKey: key, isGroup: false, members };
    }

    // Order members by investor name so the dialog's table order agrees with the
    // investor_names string (which the DB sorts by junction sequence_number, while the
    // feed only orders by payment_date and is unstable within a date).
    members.sort((a, b) => String(a.investor_name || '').localeCompare(String(b.investor_name || '')));

    const sum = (f) => +members.reduce((s, m) => s + (parseFloat(m[f]) || 0), 0).toFixed(2);

    return {
      ...head,
      _groupKey: key,
      isGroup: true,
      members,

      // ⚠ A GROUP HAS NO SCHEDULE ID. Deliberately null so any code path that forgets to
      // iterate `members` fails loudly instead of matching against an arbitrary member's
      // schedule row — a silent mis-posting would be far worse than a crash.
      id: null,

      // Identity/display carried from the head row. user_reference is the EXTERNAL reference
      // and must survive grouping: ContractReferenceCell promotes it to the primary line, so
      // dropping it here would blank the column operators actually recognise contracts by.
      contract_id: head.contract_id,
      contract_reference: head.contract_reference,
      user_reference: head.user_reference,
      contract_status: head.contract_status,
      currency: head.currency,
      narration: head.narration,
      payment_date: head.payment_date,
      days_overdue: head.days_overdue,

      scheduled_amount: sum('scheduled_amount'),
      interest_amount: sum('interest_amount'),
      capital_amount: sum('capital_amount'),
      wht_amount: sum('wht_amount'),
      gross_interest_amount: sum('gross_interest_amount'),

      /* ⚠ TABLE-FACING ALIASES MUST BE RE-DERIVED, NOT INHERITED FROM THE HEAD ROW.
       *
       * PaymentMatching maps feed rows through toTableRow BEFORE handing them here, and that shim
       * sets payment_amount = scheduled_amount. The spread above therefore carries the HEAD
       * MEMBER's payment_amount onto the group, while scheduled_amount is correctly summed — so
       * the Amount column (which reads payment_amount) showed ONE investor's share beside an
       * Outstanding derived from the group total: dev MTN-SZ-2026-0005 rendered Amount 863.28
       * against Outstanding 1,726.56, an outstanding balance larger than the amount owed, which
       * is impossible on its face.
       *
       * Any display field the tables read but this grouper does not recompute is exposed to the
       * same trap. Keep this block next to the sums so the two cannot drift apart. */
      payment_amount: sum('scheduled_amount'),
      currency_code: head.currency_code || head.currency,

      // T3-111: cash already matched across the group's members. Summed like any other money
      // column, and load-bearing rather than informational: without it a group would propose a
      // lump sum covering the FULL scheduled total even when one member is already part-settled,
      // silently asking the operator to pay money that has already been received.
      matched_amount: sum('matched_amount'),

      // T3-122: residuals rolled in from earlier short payments SUM across the group — each member
      // carries its own. Taking the head row's value (which the spread would do) would under-report
      // the group, the same defect already fixed for payment_amount.
      residual_carried_in: sum('residual_carried_in'),

      // ⚠ NOT summed and NOT taken from the head: wht_rate_applied and wht_gross_up are
      // PER INVESTOR and genuinely differ within one group (dev MTN-SZ-2026-0005 holds
      // investor 1 at 5% gross-up beside investor 10 at 0%). Averaging them, or letting the
      // head's value stand for the group, would misstate the tax basis of the other members.
      // Consumers must read these per member.
      wht_rate_applied: null,
      wht_gross_up: null,

      // All-or-nothing across the group: the combined WHT action is only offered when EVERY
      // member's tax is still outstanding (see the per-member validity rule in MatchDialog).
      wht_paid: members.every((m) => m.wht_paid === true || m.wht_paid === 'true'),

      // No single investor owns a group, so the label falls through to "Multiple (N)".
      investor_name: null,
      investor_id: null,
      is_multi_investor: true,
      investor_count: members.length,
      investor_names: members.map((m) => m.investor_name).filter(Boolean).join(', '),
    };
  });
};

/**
 * Get payment type color for MUI Chip
 * @param {string} type - Payment type or narration
 * @returns {string} MUI color name
 */
export const getPaymentTypeColor = (type) => {
  if (!type) return 'default';
  if (type.includes('Interest')) return 'info';
  if (type.includes('Capital')) return 'success';
  if (type.includes('Settlement')) return 'warning';
  if (type.includes('Withdrawal')) return 'warning';
  return 'default';
};
